import classNames from "classnames";
import * as Icons from "Components/Icons";
import { type MapPinHQProps } from "Components/Icons/MapPinHQ";
import mapboxgl from "mapbox-gl";
import * as React from "react";
import ReactDOM from "react-dom";
import { IVenueSearchResult } from "Services/API/API";
import tippy from "tippy.js";

export type MarkerIcon = "Default" | "HQ" | "Like" | "Preferred" | "GeoLocation" | "Extended" | "StreetView";

export interface CreateMarkerOptions {
   className?: string;
   renderTooltip?: () => JSX.Element;
   lat: number;
   lng: number;
   icon?: MarkerIcon;
   hqBrandingOptions?: HqBrandingnOptions;
   draggable?: boolean;
}

interface HqBrandingnOptions {
   label?: string | null,
   color?: string | null,
   colorHover?: string | null,
   logo?: string | null,
}

export function createMarker(options: CreateMarkerOptions) {
   const markerProps = (options.icon && options.icon in MarkerPinsConstants ? MarkerPinsConstants[options.icon] : MarkerPinsConstants.Default);

   const div = document.createElement("div");
   let props: Icons.IconProps2d | undefined = undefined;

   if (markerProps.icon == MarkerPinsConstants.HQ.icon) {
      props = {
         iconText: options?.hqBrandingOptions?.label,
         logo: options?.hqBrandingOptions?.logo,
      } as MapPinHQProps;
      if (options?.hqBrandingOptions?.color) {
         div.style.setProperty("--pin-color", options.hqBrandingOptions.color);
      }
      if (options?.hqBrandingOptions?.colorHover) {
         div.style.setProperty("--pin-color-hover", options.hqBrandingOptions.colorHover);
      }
   }

   div.className = classNames(
      "mapbox-marker",
      `mapbox-marker-order-${markerProps.zIndex}`,
      markerProps.className,
      { "pointer": !!options.renderTooltip },
      options.className);

   if (markerProps.icon)
      ReactDOM.render(React.createElement(markerProps.icon, props ?? {}), div);

   const marker = new mapboxgl.Marker({
      element: div,
      draggable: options.draggable || false,
      anchor: markerProps.anchor,
   })
      .setLngLat({ lat: options.lat, lng: options.lng });

   if (options.renderTooltip) {
      const popupDiv = document.createElement("div");
      let shouldRender = false;

      tippy(div, {
         content: popupDiv,
         onShow: () => {
            if (!shouldRender) {
               shouldRender = true;
               ReactDOM.render(options.renderTooltip!(), popupDiv);
            }
         },
         arrow: true,
         interactive: true,
         interactiveBorder: 0,
         maxWidth: "none",
         theme: "liquidspace-mappintooltip"

      });
      //const popup = new mapboxgl.Popup({ offset: 35, maxWidth: "385px", focusAfterOpen: false }) // add popups
      //   .once("open", () => {
      //      const popupDiv = document.createElement('div');
      //      ReactDOM.render(options.renderTooltip!(), popupDiv, () => popup.setDOMContent(popupDiv));            
      //   })
      //   .on('open', () => {
      //      marker.getElement().classList.add('mapbox-marker--active');
      //   })
      //   .on("close", () => {
      //      marker.getElement().classList.remove('mapbox-marker--active');
      //   });
      //marker.setPopup(popup);
   }

   return marker;
}

export const getLocationMarkerIcon = (venue: IVenueSearchResult, favoritesSlice?: LSWebUI.Models.FavoriteCollectionModel[] | undefined): MarkerIcon => {
   if (venue.enterpriseHeadquarter) {
      return "HQ";
   }

   if (favoritesSlice && favoritesSlice.find(c => c.Favorites.find(f => f.VenueId == venue.venueId))) {
      return "Like";
   }

   if (venue.enterprisePreferredLocation) {
      return "Preferred";
   }

   return venue.isExtendedNetwork ? "Extended" : "Default";
}

const MarkerPinsConstants: { [key in MarkerIcon]: {
   icon?: Icons.Icon2d;
   anchor: mapboxgl.Anchor;
   /// See mapbox.ls.ts
   zIndex: number;
   className?: string;
} } = {
   Default: {
      icon: Icons.MapPin,
      anchor: "center",
      zIndex: 2
   },
   HQ: {
      icon: Icons.MapPinHQ,
      anchor: "bottom",
      zIndex: 6
   },
   Like: {
      icon: Icons.MapPinLike,
      anchor: "bottom",
      zIndex: 4
   },
   Preferred: {
      icon: Icons.MapPinPreferred,
      anchor: "bottom",
      zIndex: 5
   },
   GeoLocation: {
      icon: Icons.GeoLocationPin,
      anchor: "center",
      zIndex: 3,
      className: "mapbox-marker-geo-location"
   },
   Extended: {
      icon: Icons.MapPinExtended,
      anchor: "center",
      zIndex: 1,
      className: "mapbox-marker-extended"
   },
   StreetView: {
      icon: undefined,
      anchor: "bottom",
      zIndex: 1,
      className: "mapbox-marker-icon-streetView"
   }
}